import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { FaPlus, FaEdit, FaTrash, FaEye, FaTimes, FaSave, FaFileInvoice, FaSearch, FaPrint, FaCheck, FaBan } from 'react-icons/fa';
import LedgerModal from '../../components/common/LedgerModal';
import api from '../../services/api';
import Loading from '../../components/common/Loading';
import './Billing.css';
import jsPDF from 'jspdf';
import logo from '../../assets/img.jpg';
import { formatShortDate } from '../../utils/date';

const formatDateDDMMYYYY = (date) => {
  if (!date) return '';
  const d = new Date(date);
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yyyy = d.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
};

const Billing = () => {
  const [billings, setBillings] = useState([]);
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [companies, setCompanies] = useState([]);
  const [inventoryItems, setInventoryItems] = useState([]);
  const [quotations, setQuotations] = useState([]);
  const [search, setSearch] = useState('');
  const [companyFilter, setCompanyFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingBilling, setEditingBilling] = useState(null);
  const [viewingBilling, setViewingBilling] = useState(null);
  const [showLedgerModal, setShowLedgerModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [inventoryPage, setInventoryPage] = useState(1);
  const inventoryItemsPerPage = 20;

  const [customers, setCustomers] = useState([]);
  const [customerSearch, setCustomerSearch] = useState('');
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [activeItemIndex, setActiveItemIndex] = useState(-1);
  const [activeCustomerIndex, setActiveCustomerIndex] = useState(-1);

  const [formData, setFormData] = useState({
    company: '',
    quotation: '',
    items: [],
    notes: '',
    paymentMethod: 'cash',
    paymentStatus: 'due',
    amountPaid: 0,
    creditDays: 0,
    dueDate: '',
    deliveryCharge: 0,
    additionalCharges: 0,
    customer: '',
    customerName: '',
    customerPhone: '',
    customerStreet: '',
    customerCity: '',
    customerState: '',
    customerZipCode: '',
    customerCountry: '',
    transfoter: '',
    previousBalance: 0,
    previousBalanceDate: ''
  });

  const [newItem, setNewItem] = useState({
    inventory: '',
    quantity: 1,
    sellingPrice: 0,
    discount: 0
  });

  const [itemSearchQuery, setItemSearchQuery] = useState('');
  const [showItemDropdown, setShowItemDropdown] = useState(false);
  const [filteredInventoryItems, setFilteredInventoryItems] = useState([]);
  const [showPrintConfirm, setShowPrintConfirm] = useState(false);
  const [lastCreatedBilling, setLastCreatedBilling] = useState(null);
  const itemInputRef = useRef(null);

  const handleAppendPreviousData = async () => {
    if (!editingBilling || !editingBilling._id) return;
    try {
      setLoading(true);
      const response = await api.get(`/billing/${editingBilling._id}`);
      const fresh = response.data?.billing;
      if (!fresh) return;
      const normalizedItems = (fresh.items || []).map((item) => {
        const inv = item.inventory;
        const inventoryId = (inv && inv._id) ? inv._id : (item.inventory || '');
        const sellingPriceNum = Number(item.sellingPrice || inv?.sellingPrice || 0);
        const quantityNum = Number(item.quantity || 1);
        const discountNum = Number(item.discount || 0);
        const totalAmountNum = (sellingPriceNum - (sellingPriceNum * discountNum / 100)) * quantityNum;
        return {
          inventory: inventoryId,
          quantity: quantityNum,
          sellingPrice: sellingPriceNum,
          discount: discountNum,
          totalAmount: Number(item.totalAmount ?? totalAmountNum),
          itemName: inv?.itemName || item.itemName || '',
          itemCode: inv?.itemCode || item.itemCode || ''
        };
      });

      // Merge DB items with current form items (append)
      const mergedItems = [...normalizedItems, ...(formData.items || [])];

      // Recalculate totals
      const subtotal = mergedItems.reduce((sum, it) => sum + (Number(it.sellingPrice || 0) * Number(it.quantity || 0)), 0);
      const totalDiscount = mergedItems.reduce((sum, it) => sum + ((Number(it.sellingPrice || 0) * Number(it.discount || 0) / 100) * Number(it.quantity || 0)), 0);
      const totalAmount = subtotal - totalDiscount;

      // Normalize amountPaid by paymentStatus selection
      let normalizedAmountPaid = Number(formData.amountPaid ?? fresh.amountPaid ?? 0);
      if (formData.paymentStatus === 'full') normalizedAmountPaid = totalAmount;
      if (formData.paymentStatus === 'due') normalizedAmountPaid = 0;

      // Prepare payload expected by backend update
      const updatePayload = {
        company: formData.company || fresh.company?._id || fresh.company,
        quotation: formData.quotation || fresh.quotation?._id || fresh.quotation || undefined,
        items: mergedItems.map(it => ({
          inventory: it.inventory,
          quantity: it.quantity,
          sellingPrice: it.sellingPrice,
          discount: it.discount
        })),
        notes: formData.notes ?? fresh.notes,
        paymentMethod: formData.paymentMethod ?? fresh.paymentMethod,
        paymentStatus: formData.paymentStatus ?? fresh.paymentStatus,
        amountPaid: normalizedAmountPaid,
        creditDays: formData.creditDays ?? fresh.creditDays ?? 0,
        dueDate: formData.dueDate || fresh.dueDate || new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
        // Customer snapshot
        customerName: formData.customerName || fresh.customerName || '',
        customer: formData.customer || fresh.customer,
        customerPhone: formData.customerPhone || fresh.customerPhone || '',
        customerAddress: {
          street: formData.customerStreet || fresh.customerAddress?.street || '',
          city: formData.customerCity || fresh.customerAddress?.city || '',
          state: formData.customerState || fresh.customerAddress?.state || '',
          zipCode: formData.customerZipCode || fresh.customerAddress?.zipCode || '',
          country: formData.customerCountry || fresh.customerAddress?.country || ''
        },
        // Totals
        additionalCharges: Number(formData.additionalCharges || fresh.additionalCharges || 0),
        subtotal,
        totalDiscount,
        totalAmount
      };

      const updateRes = await api.put(`/api/billing/${editingBilling._id}`, updatePayload);
      const updated = updateRes.data?.billing || null;

      // Update local table
      if (updated) {
        setBillings(prev => prev.map(b => b._id === updated._id ? updated : b));
      } else {
        setBillings(prev => prev.map(b => {
          if (b._id !== editingBilling._id) return b;
          return { ...b, items: mergedItems, subtotal, totalDiscount, totalAmount, amountPaid: normalizedAmountPaid };
        }));
      }

      // Reset form to reflect DB items after append
      setFormData(prev => ({ ...prev, items: mergedItems, amountPaid: normalizedAmountPaid }));

      // Close modal after adding
      setShowModal(false);
      setEditingBilling(null);
    } catch (err) {
      console.error('Error appending data to billing:', err);
      setError('Failed to add data to billing');
    } finally {
      setLoading(false);
    }
  };

  const paymentMethods = [
    { value: 'cash', label: 'Cash' },
    { value: 'card', label: 'Card' },
    { value: 'bank_transfer', label: 'Bank Transfer' },
    { value: 'cheque', label: 'Cheque' },
    { value: 'online', label: 'Online' }
  ];

  const paymentStatusOptions = [
    { value: 'full', label: 'Full Payment' },
    { value: 'partial', label: 'Partial Payment' },
    { value: 'due', label: 'Due Payment' }
  ];

  const statusOptions = [
    { value: 'pending', label: 'Pending' },
    { value: 'paid', label: 'Paid' },
    { value: 'overdue', label: 'Overdue' },
    { value: 'cancelled', label: 'Cancelled' }
  ];

  useEffect(() => {
    loadBillings();
    loadCompanies();
    loadCustomers();
    loadInventoryItems();
    loadQuotations();
  }, []);

  // Reset/clamp page when filters or data change
  useEffect(() => {
    setCurrentPage(1);
  }, [search, companyFilter, statusFilter]);

  useEffect(() => {
    const totalPages = Math.max(1, Math.ceil(filteredBillings.length / itemsPerPage));
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [billings, currentPage, itemsPerPage]);

  const loadBillings = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (companyFilter) params.append('company', companyFilter);
      if (statusFilter) params.append('status', statusFilter);
      if (search) params.append('search', search);

      const response = await api.get(`/api/billing?${params.toString()}`);
      const rawBillings = response.data.billings || [];

      const normalized = rawBillings.map(b => ({
        ...b,
        amountPaid: Number(b?.amountPaid ?? 0)
      }));

      // Sort: Today's records first, then by date descending
      const today = new Date().setHours(0, 0, 0, 0);
      const sorted = [...normalized].sort((a, b) => {
        const dateA = new Date(a.createdAt).setHours(0, 0, 0, 0);
        const dateB = new Date(b.createdAt).setHours(0, 0, 0, 0);

        if (dateA === today && dateB !== today) return -1;
        if (dateA !== today && dateB === today) return 1;

        return new Date(b.createdAt) - new Date(a.createdAt);
      });

      setBillings(sorted);
    } catch (err) {
      console.error('Error loading billings:', err);
      setError('Failed to load billings');
    } finally {
      setLoading(false);
    }
  };

  const loadCompanies = async () => {
    try {
      const response = await api.get('/api/companies');
      setCompanies(response.data.companies || []);
    } catch (err) {
      console.error('Error loading companies:', err);
    }
  };

  const loadCustomers = async () => {
    try {
      const response = await api.get('/api/customers?limit=1000');
      setCustomers(response.data.customers || []);
    } catch (err) {
      console.error('Error loading customers:', err);
    }
  };

  const loadInventoryItems = async () => {
    try {
      const response = await api.get('/api/inventory');
      setInventoryItems(response.data.inventoryItems || []);
      setInventoryPage(1);
    } catch (err) {
      console.error('Error loading inventory items:', err);
    }
  };

  const loadQuotations = async () => {
    try {
      const response = await api.get('/api/quotations');
      console.log('Quotations loaded:', response.data.quotations);
      setQuotations(response.data.quotations || []);
    } catch (err) {
      console.error('Error loading quotations:', err);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this billing?')) {
      try {
        await api.delete(`/api/billing/${id}`);
        setBillings(billings.filter(b => b._id !== id));
      } catch (err) {
        console.error('Error deleting billing:', err);
        setError('Failed to delete billing');
      }
    }
  };

  const openCreateModal = () => {
    setFormData({
      company: '',
      quotation: '',
      items: [],
      notes: '',
      paymentMethod: 'cash',
      paymentStatus: 'due',
      amountPaid: 0,
      creditDays: 0,
      dueDate: '',
      deliveryCharge: 0,
      additionalCharges: 0,
      customer: '',
      customerName: '',
      customerPhone: '',
      customerStreet: '',
      customerCity: '',
      customerState: '',
      customerZipCode: '',
      customerCountry: '',
      transfoter: '',
      previousBalance: 0,
      previousBalanceDate: ''
    });
    setCustomerSearch('');
    setItemSearchQuery('');
    setShowItemDropdown(false);
    setShowCreateModal(true);
    setError('');
  };

  const openEditModal = async (billing) => {
    try {
      setLoading(true);
      setError('');
      const resp = await api.get(`/api/billing/${billing._id}`);
      const fresh = resp.data?.billing || billing;
      const normalizedItems = (fresh.items || []).map((item) => {
        const inv = item.inventory;
        const inventoryId = (inv && inv._id) ? inv._id : (item.inventory || '');
        const sellingPriceNum = Number(item.sellingPrice || inv?.sellingPrice || 0);
        const quantityNum = Number(item.quantity || 1);
        const discountNum = Number(item.discount || 0);
        const totalAmountNum = (sellingPriceNum - (sellingPriceNum * discountNum / 100)) * quantityNum;
        return {
          inventory: inventoryId,
          quantity: quantityNum,
          sellingPrice: sellingPriceNum,
          discount: discountNum,
          totalAmount: Number(item.totalAmount ?? totalAmountNum),
          itemName: inv?.itemName || item.itemName || '',
          itemCode: inv?.itemCode || item.itemCode || ''
        };
      });

      setEditingBilling(fresh);
      setFormData({
        company: fresh.company?._id || fresh.company || '',
        quotation: fresh.quotation?._id || fresh.quotation || '',
        items: normalizedItems,
        notes: fresh.notes || '',
        paymentMethod: fresh.paymentMethod || 'cash',
        paymentStatus: fresh.paymentStatus || 'due',
        amountPaid: fresh.amountPaid ?? 0,
        creditDays: fresh.creditDays ?? 0,
        dueDate: fresh.dueDate ? new Date(fresh.dueDate).toISOString().split('T')[0] : '',
        deliveryCharge: fresh.deliveryCharge || 0,
        additionalCharges: fresh.additionalCharges || 0,
        customer: fresh.customer?._id || fresh.customer || '',
        customerName: fresh.customerName || '',
        customerPhone: fresh.customerPhone || '',
        customerStreet: fresh.customerAddress?.street || '',
        customerCity: fresh.customerAddress?.city || '',
        customerState: fresh.customerAddress?.state || '',
        customerZipCode: fresh.customerAddress?.zipCode || '',
        customerCountry: (fresh.customerAddress?.country || ''),
        transfoter: (fresh.transfoter || ''),
        previousBalance: Number(fresh.previousBalance || 0),
        previousBalanceDate: fresh.previousBalanceDate ? new Date(fresh.previousBalanceDate).toISOString().split('T')[0] : ''
      });
      setCustomerSearch(fresh.customerName || '');
      setShowModal(true);
    } catch (err) {
      console.error('Error opening edit modal:', err);
      // Fallback to existing object
      setEditingBilling(billing);
      setFormData({
        company: billing.company?._id || billing.company || '',
        quotation: billing.quotation?._id || billing.quotation || '',
        items: (billing.items || []).map((item) => ({
          inventory: item.inventory?._id || item.inventory || '',
          quantity: Number(item.quantity || 1),
          sellingPrice: Number(item.sellingPrice || 0),
          discount: Number(item.discount || 0),
          totalAmount: Number(item.totalAmount || 0),
          itemName: item.inventory?.itemName || item.itemName || '',
          itemCode: item.inventory?.itemCode || item.itemCode || ''
        })),
        notes: billing.notes || '',
        paymentMethod: billing.paymentMethod || 'cash',
        paymentStatus: billing.paymentStatus || 'due',
        amountPaid: billing.amountPaid ?? 0,
        creditDays: billing.creditDays ?? 0,
        dueDate: billing.dueDate ? new Date(billing.dueDate).toISOString().split('T')[0] : '',
        deliveryCharge: billing.deliveryCharge || 0,
        additionalCharges: billing.additionalCharges || 0,
        customer: billing.customer?._id || billing.customer || '',
        customerName: billing.customerName || '',
        customerPhone: billing.customerPhone || '',
        customerStreet: billing.customerAddress?.street || '',
        customerCity: billing.customerAddress?.city || '',
        customerState: billing.customerAddress?.state || '',
        customerZipCode: billing.customerAddress?.zipCode || '',
        customerCountry: billing.customerAddress?.country || '',
        transfoter: billing.transfoter || '',
        previousBalance: billing.previousBalance || 0,
        previousBalanceDate: billing.previousBalanceDate ? new Date(billing.previousBalanceDate).toISOString().split('T')[0] : ''
      });
      setCustomerSearch(billing.customerName || '');
      setShowModal(true);
    } finally {
      setLoading(false);
    }
  };

  const openViewModal = (billing) => {
    setViewingBilling(billing);
  };

  const handlePrint = (billing) => {
    if (!billing) return;

    const subtotal = Number(billing.subtotal || 0);
    const discount = Number(billing.totalDiscount || 0);
    const deliveryCharge = Number(billing.deliveryCharge || 0);
    const additionalCharges = Number(billing.additionalCharges || 0);
    const previousBalance = Number(billing.previousBalance || 0);
    const total = Number(billing.totalAmount || (subtotal - discount + deliveryCharge + additionalCharges + previousBalance));

    // Match PDF calculation for rounding and bill value
    const gross = subtotal - discount + deliveryCharge + previousBalance;
    const roundedBillValue = Math.round(gross);
    const roundOff = (roundedBillValue - gross).toFixed(2);

    const numberToIndianWords = (amount) => {
      const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
      const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
      const two = (num) => {
        if (num < 20) return ones[num];
        const t = Math.floor(num / 10);
        const o = num % 10;
        return `${tens[t]}${o ? ' ' + ones[o] : ''}`.trim();
      };
      const three = (num) => {
        const h = Math.floor(num / 100);
        const r = num % 100;
        const hPart = h ? `${ones[h]} Hundred${r ? ' ' : ''}` : '';
        return `${hPart}${two(r)}`.trim();
      };
      const n = Math.round(Number(amount || 0));
      if (n === 0) return 'Zero';
      const crore = Math.floor(n / 10000000);
      const lakh = Math.floor((n % 10000000) / 100000);
      const thousand = Math.floor((n % 100000) / 1000);
      const hundred = Math.floor((n % 1000));
      const parts = [];
      if (crore) parts.push(`${two(crore)} Crore`);
      if (lakh) parts.push(`${two(lakh)} Lakh`);
      if (thousand) parts.push(`${two(thousand)} Thousand`);
      if (hundred) parts.push(three(hundred));
      return parts.join(' ').replace(/\s+/g, ' ').trim();
    };

    const toLines = [
      billing.customerName || '',
      [billing.customerAddress?.street, billing.customerAddress?.city].filter(Boolean).join(', '),
      [billing.customerAddress?.state, billing.customerAddress?.zipCode].filter(Boolean).join(', ')
    ].filter(Boolean);

    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>Bill ${billing.billingNo}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; color: #333; }
            .header { text-align: center; margin-bottom: 20px; border-bottom: 2px solid #eee; padding-bottom: 10px; }
            .meta { display: flex; justify-content: space-between; margin-bottom: 20px; }
            .meta-column { flex: 1; }
            .meta-column + .meta-column { padding-left: 20px; }
            .meta-box { border: 1px solid #ccc; padding: 8px; width: 180px; }
            .meta-box div { margin-bottom: 4px; }
            table { width: 100%; border-collapse: collapse; margin-top: 20px; }
            th, td { border: 1px solid #ddd; padding: 8px; }
            th { background-color: #f8f9fa; text-align: left; }
            .totals { margin-top: 20px; float: right; width: 340px; }
            .total-row { display: flex; justify-content: space-between; padding: 6px 0; border-bottom: 1px solid #f1f1f1; }
            .grand-total { border-top: 2px solid #333; margin-top: 10px; padding-top: 10px; font-weight: bold; font-size: 1.1em; }
            .note { margin-top: 40px; border-top: 1px solid #ddd; padding-top: 10px; }
            @media print { .no-print { display: none; } }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>ESTIMATE</h1>
            <h3>${billing.billingNo}</h3>
          </div>
          <div class="meta">
            <div class="meta-column">
              <p><strong>To:</strong><br>
                ${toLines.map(l => `${l}<br>`).join('')}
              </p>
            </div>
            <div class="meta-column" style="text-align: right;">
              <div class="meta-box">
                <div><strong>Bill No:</strong> ${billing.billingNo || ''}</div>
                <div><strong>Dt:</strong> ${formatDateDDMMYYYY(billing.createdAt || billing.date)}</div>
                <div><strong>Transporter:</strong> ${billing.transfoter || ''}</div>
              </div>
            </div>
          </div>
          <table>
            <thead>
              <tr>
                <th>Sl.</th>
                <th>Product Name</th>
                <th>Ctn</th>
                <th>Qty</th>
                <th>Rate</th>
                <th>Disc%</th>
                <th>Amount</th>
              </tr>
            </thead>
            <tbody>
              ${Array.isArray(billing.items) ? billing.items.map((item, idx) => {
                const name = item.inventory?.itemName || item.itemName || '';
                const qty = Number(item.quantity || 0);
                const rate = Number(item.sellingPrice || 0);
                const disc = Number(item.discount || 0);
                const amount = (rate - rate * (disc / 100)) * qty;
                const maxNameChars = 48;
                const clippedName = String(name).length > maxNameChars ? String(name).slice(0, maxNameChars - 1) + '…' : String(name);
                return `
                  <tr>
                    <td>${idx + 1}</td>
                    <td>${clippedName}</td>
                    <td></td>
                    <td style="text-align: right;">${qty}</td>
                    <td style="text-align: right;">${rate.toFixed(2)}</td>
                    <td style="text-align: right;">${disc ? disc.toFixed(0) : ''}</td>
                    <td style="text-align: right;">${amount.toFixed(2)}</td>
                  </tr>`;
              }).join('') : ''}
            </tbody>
          </table>
          <div class="totals">
            <div class="total-row"><span>Subtotal:</span><span>₹${subtotal.toFixed(2)}</span></div>
            <div class="total-row"><span>Discount:</span><span>₹${discount.toFixed(2)}</span></div>
            <div class="total-row"><span>Total:</span><span>₹${total.toFixed(2)}</span></div>
            ${deliveryCharge ? `<div class="total-row"><span>Add : Delivery Charges</span><span>₹${deliveryCharge.toFixed(2)}</span></div>` : ''}
            ${additionalCharges ? `<div class="total-row"><span>Add : Company Amount<br/><small>Current Date: ${formatDateDDMMYYYY(billing.createdAt)}</small></span><span>₹${additionalCharges.toFixed(2)}</span></div>` : ''}
            <div class="total-row"><span>Add : Balance ${formatDateDDMMYYYY(billing.previousBalanceDate || billing.balanceDate || billing.createdAt)}</span><span>₹${previousBalance.toFixed(2)}</span></div>
            ${billing.transfoter ? `<div class="total-row"><span>Transporter:</span><span>${billing.transfoter}</span></div>` : ''}
            <div class="total-row"><span>Round Off</span><span>₹${roundOff}</span></div>
            <div class="total-row grand-total"><span>Bill Value</span><span>₹${roundedBillValue.toFixed(2)}</span></div>
            <div class="total-row" style="font-size: 0.9em;"><span>In Words:</span><span>${numberToIndianWords(roundedBillValue)} Only</span></div>
            <div class="total-row"><span>Paid:</span><span>₹${Number(billing.amountPaid || 0).toFixed(2)}</span></div>
            <div class="total-row" style="color: #d9534f; border-bottom: none;"><span>Balance:</span><span>₹${(Math.max(0, roundedBillValue - (billing.amountPaid || 0))).toFixed(2)}</span></div>
          </div>
          ${billing.notes ? `<div class="note"><strong>Notes:</strong><br><p>${billing.notes}</p></div>` : ''}
        </body>
      </html>
    `);
    printWindow.document.close();
    setTimeout(() => {
      printWindow.print();
    }, 500);
  };

  const openLedgerForBilling = () => {
    setShowLedgerModal(true);
  };

  const formatCurrency = (n) => `₹${Number(n || 0).toFixed(2)}`;

  const handleDownloadPdf = (billing) => {
    if (!billing) return;
    // A4 portrait to match the sample photo
    const doc = new jsPDF({ orientation: 'p', unit: 'mm', format: 'a4' });

    // Helpers
    const left = 10;
    const right = doc.internal.pageSize.getWidth() - 12; // a bit more right padding to avoid clipping
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const col = (x) => left + x; // convenience column calculator
    const formatMoney = (n) => Number(n || 0).toFixed(2);
    const padPcs = (n) => `${Number(n || 0).toFixed(2)}Pcs`;
    const formatDateDDMMYYYY = (d) => {
      if (!d) return '';
      const dt = new Date(d);
      const dd = String(dt.getDate()).padStart(2, '0');
      const mm = String(dt.getMonth() + 1).padStart(2, '0');
      const yyyy = dt.getFullYear();
      return `${dd}/${mm}/${yyyy}`;
    };
    const numberToIndianWords = (amount) => {
      // Supports up to Crores comfortably; tailored to Indian system used in the sample
      const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
      const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
      const two = (num) => {
        if (num < 20) return ones[num];
        const t = Math.floor(num / 10);
        const o = num % 10;
        return `${tens[t]}${o ? ' ' + ones[o] : ''}`.trim();
      };
      const three = (num) => {
        const h = Math.floor(num / 100);
        const r = num % 100;
        const hPart = h ? `${ones[h]} Hundred${r ? ' ' : ''}` : '';
        return `${hPart}${two(r)}`.trim();
      };
      const n = Math.round(Number(amount || 0));
      if (n === 0) return 'Zero';
      const crore = Math.floor(n / 10000000);
      const lakh = Math.floor((n % 10000000) / 100000);
      const thousand = Math.floor((n % 100000) / 1000);
      const hundred = Math.floor((n % 1000));
      const parts = [];
      if (crore) parts.push(`${two(crore)} Crore`);
      if (lakh) parts.push(`${two(lakh)} Lakh`);
      if (thousand) parts.push(`${two(thousand)} Thousand`);
      if (hundred) parts.push(three(hundred));
      return parts.join(' ').replace(/\s+/g, ' ').trim();
    };

    // Compute figures
    const subtotal = Number(billing.subtotal || 0);
    const discount = Number(billing.totalDiscount || 0);
    const total = Number(billing.totalAmount || (subtotal - discount));
    const deliveryCharge = Number(billing.deliveryCharge || 0);
    const previousBalance = Number(billing.previousBalance || 0);
    const gross = subtotal - discount + deliveryCharge + previousBalance;
    const roundedBillValue = Math.round(gross);
    const roundOff = (roundedBillValue - gross).toFixed(2);
    const additionalCharges = Number(billing.additionalCharges || 0);
    // Note: totalAmount in backend already considers additionalCharges if calculated there, but here we reconstruct
    // to match PDF logic. If totalAmount includes charges, we are good.
    // Re-check calculation logic:
    // in calculateTotals: total = sub - disc + del + add
    // So gross matches.

    // Title: ESTIMATE
    let y = 10;
    doc.setFontSize(12);
    doc.setFont(undefined, 'bold');
    doc.text('ESTIMATE', pageWidth / 2, y, { align: 'center' });

    // Header left: To, customer
    y += 10;
    doc.setFontSize(10);
    doc.setFont(undefined, 'normal');
    doc.text('To,', left, y);
    y += 6;
    const toLines = [
      billing.customerName || '',
      [
        billing.customerAddress?.street,
        billing.customerAddress?.city
      ].filter(Boolean).join(', '),
      [
        billing.customerAddress?.state,
        billing.customerAddress?.zipCode
      ].filter(Boolean).join(', ')
    ].filter(Boolean);
    toLines.forEach((line) => { doc.text(String(line), left, y); y += 5; });

    // Header right box: Bill No, Dt, Transpoter
    const boxTop = 18;
    const boxLeft = right - 70;
    const boxWidth = 70;
    const boxHeight = 20;
    doc.setLineWidth(0.2);
    doc.rect(boxLeft, boxTop, boxWidth, boxHeight);
    doc.setFont(undefined, 'bold');
    doc.text('Bill No:', boxLeft + 2, boxTop + 6);
    doc.text('Dt:', boxLeft + 2, boxTop + 12);
    doc.text('Transpoter:', boxLeft + 2, boxTop + 18);
    doc.setFont(undefined, 'normal');
    doc.text(String(billing.billingNo || ''), boxLeft + 30, boxTop + 6);
    doc.text(formatDateDDMMYYYY(billing.createdAt || billing.date), boxLeft + 30, boxTop + 12);
    doc.text(String(billing.transfoter || ''), boxLeft + 30, boxTop + 18);

    // Table header (with dotted verticals like the sample)
    y = 48;
    const tableLeft = left;
    const tableRight = right;
    // Column widths must sum to available width (right - left)
    // Available = pageWidth - 22 (~190mm with our margins)
    const colWidths = {
      sl: 8,
      product: 82,
      ctn: 14,
      qty: 20,
      rate: 26,
      disc: 16,
      amount: 24
    };
    const colX = {
      sl: tableLeft,
      product: tableLeft + colWidths.sl,
      ctn: tableLeft + colWidths.sl + colWidths.product,
      qty: tableLeft + colWidths.sl + colWidths.product + colWidths.ctn,
      rate: tableLeft + colWidths.sl + colWidths.product + colWidths.ctn + colWidths.qty,
      disc: tableLeft + colWidths.sl + colWidths.product + colWidths.ctn + colWidths.qty + colWidths.rate,
      amount: tableLeft + colWidths.sl + colWidths.product + colWidths.ctn + colWidths.qty + colWidths.rate + colWidths.disc
    };
    const drawTableHeader = (yStart) => {
      doc.setFontSize(10);
      doc.setFont(undefined, 'bold');
      doc.text('Sl.', colX.sl + 1, yStart);
      doc.text('Product Name', colX.product + 1, yStart);
      doc.text('Ctn', colX.ctn + 1, yStart);
      doc.text('Qty', colX.qty + colWidths.qty - 2, yStart, { align: 'right' });
      doc.text('Rate', colX.rate + colWidths.rate - 2, yStart, { align: 'right' });
      doc.text('Disc%', colX.disc + colWidths.disc - 2, yStart, { align: 'right' });
      doc.text('Amount', colX.amount + colWidths.amount - 2, yStart, { align: 'right' });
      // Top dotted divider under header
      doc.setLineDash([1, 1], 0);
      doc.line(tableLeft, yStart + 2, tableRight, yStart + 2);
      doc.setLineDash();
      // Column dotted separators down to footer separator height
      const colBottom = pageHeight - 70;
      const drawCol = (x) => {
        doc.setLineDash([1, 1], 0);
        doc.line(x, yStart - 6, x, colBottom);
        doc.setLineDash();
      };
      drawCol(colX.product);
      drawCol(colX.ctn);
      drawCol(colX.qty);
      drawCol(colX.rate);
      drawCol(colX.disc);
      drawCol(colX.amount);
    };
    drawTableHeader(y);

    // Items rows
    let iy = y + 8;
    doc.setFontSize(8.6); // slightly smaller to ensure numeric columns fit
    doc.setFont(undefined, 'normal');
    const items = Array.isArray(billing.items) ? billing.items : [];
    const maxRows = 20; // exactly 20 items per page
    let rowIndex = 0;

    const renderRow = (idx, item) => {
      const name = item.inventory?.itemName || item.itemName || '';
      const qty = item.quantity || 0;
      const rate = Number(item.sellingPrice || 0);
      const disc = Number(item.discount || 0);
      const amount = (rate - rate * (disc / 100)) * qty;
      // Clip long product names to available space so numeric columns never shift
      const maxNameChars = 48;
      const clippedName = String(name).length > maxNameChars ? String(name).slice(0, maxNameChars - 1) + '…' : String(name);
      doc.text(String(idx + 1), colX.sl + 1, iy);
      doc.text(clippedName, colX.product + 1, iy, { maxWidth: colWidths.product - 2 });
      // Ctn not tracked; leave blank to mimic sample layout
      doc.text(padPcs(qty), colX.qty + colWidths.qty - 2, iy, { align: 'right' });
      // Use monospaced digits for numeric columns for tighter/consistent width
      doc.setFont(undefined, 'normal');
      doc.text(formatMoney(rate), colX.rate + colWidths.rate - 0.5, iy, { align: 'right' });
      doc.text(String(disc ? disc.toFixed(0) : ''), colX.disc + colWidths.disc - 2, iy, { align: 'right' });
      doc.text(formatMoney(amount), colX.amount + colWidths.amount - 0.5, iy, { align: 'right' });
    };

    const addFooterAndMaybeNewPage = (isLastPage) => {
      // Bottom dashed separator like in sample
      const sepY = pageHeight - 70;
      doc.setLineDash([1.5, 1.5], 0);
      doc.line(tableLeft, sepY, tableRight, sepY);
      doc.setLineDash();

      // Footer values block on the right
      let fy = sepY + 8;
      const label = (t) => { doc.text(t, right - 70, fy); };
      const value = (v) => { doc.text(String(v), right, fy, { align: 'right' }); };
      doc.setFont(undefined, 'normal');
      label('%:'); value(''); fy += 8; // matches photo having a standalone % label
      label('Add :Delivery Charges'); value(formatMoney(deliveryCharge)); fy += 8;
      label('Add :Company Amount'); value(formatMoney(additionalCharges)); fy += 5;
      doc.setFontSize(8); doc.text(`Current Date: ${formatDateDDMMYYYY(new Date())}`, right - 70, fy); doc.setFontSize(10); fy += 8;
      const balDate = billing.previousBalanceDate || billing.balanceDate || billing.createdAt;
      label(`Add :Balance    ${formatDateDDMMYYYY(balDate)}`); value(formatMoney(previousBalance)); fy += 10;
      if (billing.transfoter) {
        label(`Transporter: ${billing.transfoter}`); value(''); fy += 8;
      }
      label('Round Off'); value(formatMoney(roundOff)); fy += 10;
      doc.setFont(undefined, 'bold');
      label('Bill Value'); value(formatMoney(roundedBillValue));

      // Amount in words (Indian system) at the very bottom-left
      const words = `${numberToIndianWords(roundedBillValue)} Only.`;
      doc.setFont(undefined, 'normal');
      doc.text(words, tableLeft, pageHeight - 15);

      if (billing.notes) {
        doc.setFontSize(8);
        doc.text(`Notes: ${billing.notes}`, tableLeft, pageHeight - 22);
        doc.setFontSize(10);
      }
    };

    for (let i = 0; i < items.length; i++) {
      renderRow(i, items[i]);
      rowIndex++;
      iy += 6; // row height similar to sample
      if (rowIndex % maxRows === 0 && i < items.length - 1) {
        // new page: redraw table header and guides only (footer only on last page)
        doc.addPage();
        y = 20;
        drawTableHeader(y);
        iy = y + 8;
      }
    }

    // Totals area (dedicated block on the right, visually separated from table rows)
    const totalsTopY = Math.min(iy + 6, pageHeight - 90);
    const totalsBlockLeft = right - 80;
    const totalsBlockTop = totalsTopY - 4;
    const totalsBlockHeight = (pageHeight - 70) - totalsBlockTop - 16; // allow space for footer labels
    // Clear dotted column guides underneath totals using white fill
    doc.setFillColor(255, 255, 255);
    doc.rect(totalsBlockLeft, totalsBlockTop, 82, Math.max(18, totalsBlockHeight), 'F');
    // Separator above totals block
    doc.setLineDash([1.5, 1.5], 0);
    doc.line(left, totalsTopY - 2, right, totalsTopY - 2);
    doc.setLineDash();
    // Render totals inside the cleared block
    doc.setFont(undefined, 'normal');
    let ty = totalsTopY;
    const writeTotal = (label, val, bold = false) => {
      doc.setFont(undefined, bold ? 'bold' : 'normal');
      doc.text(label, right - 70, ty);
      doc.text(String(val), right, ty, { align: 'right' });
      ty += 6;
    };
    writeTotal('Subtotal', formatMoney(subtotal));
    writeTotal('Discount', formatMoney(discount));
    writeTotal('Total', formatMoney(total), true);

    // Footer (delivery, balance, round off, bill value, words)
    addFooterAndMaybeNewPage(true);

    doc.save(`Bill-${billing.billingNo || 'billing'}.pdf`);
  };

  const closeModal = () => {
    setShowModal(false);
    setShowCreateModal(false);
    setEditingBilling(null);
    setViewingBilling(null);
    setError('');
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    // If amountPaid changes, auto-adjust paymentStatus based on totals
    if (name === 'amountPaid') {
      const numericPaid = Number(value || 0);
      const { totalAmount } = calculateTotals();
      let nextStatus = formData.paymentStatus;
      if (numericPaid >= totalAmount && totalAmount > 0) {
        nextStatus = 'full';
      } else if (numericPaid === 0) {
        nextStatus = 'due';
      } else if (numericPaid > 0) {
        nextStatus = 'partial';
      }
      setFormData(prev => ({
        ...prev,
        amountPaid: value,
        paymentStatus: nextStatus,
      }));
      return;
    }

    // If paymentStatus changes, normalize amountPaid accordingly
    if (name === 'paymentStatus') {
      const nextStatus = value;
      const { totalAmount } = calculateTotals();
      let normalizedPaid = formData.amountPaid;
      if (nextStatus === 'full') {
        normalizedPaid = totalAmount;
      } else if (nextStatus === 'due') {
        normalizedPaid = 0;
      }
      setFormData(prev => ({
        ...prev,
        paymentStatus: nextStatus,
        amountPaid: normalizedPaid,
      }));
      return;
    }

    // Generic handler for all other fields
    setFormData(prev => {
      const nextData = { ...prev, [name]: value };

      // If we change a numeric field that affects the grand total, 
      // we might want to keep amountPaid in sync if it was in 'full' status
      const isNumericCharge = ['deliveryCharge', 'additionalCharges', 'previousBalance'].includes(name);
      if (isNumericCharge && prev.paymentStatus === 'full') {
        // We'll let the user manually adjust or re-select status if they want, 
        // but for now just ensure typing is smooth
      }

      return nextData;
    });

    // If quotation is selected, populate the form with quotation data
    if (name === 'quotation' && value) {
      const selectedQuotation = quotations.find(q => q._id === value);
      if (selectedQuotation) {
        setFormData(prev => ({
          ...prev,
          company: selectedQuotation.company?._id || selectedQuotation.company || '',
          items: (selectedQuotation.items || []).map(item => {
            const inv = item.inventory;
            const inventoryId = (inv && inv._id) ? inv._id : inv || '';
            return {
              inventory: inventoryId,
              quantity: item.quantity,
              sellingPrice: item.sellingPrice,
              discount: item.discount,
              totalAmount: item.totalAmount,
              itemName: inv?.itemName || item.itemName,
              itemCode: inv?.itemCode || item.itemCode
            };
          }),
          // Pre-fill customer snapshot from quotation's company if available
          customerName: selectedQuotation.company?.name || prev.customerName || '',
          customerPhone: selectedQuotation.company?.contact?.phone || prev.customerPhone || '',
          customerStreet: selectedQuotation.company?.address?.street || prev.customerStreet || '',
          customerCity: selectedQuotation.company?.address?.city || prev.customerCity || '',
          customerState: selectedQuotation.company?.address?.state || prev.customerState || '',
          customerZipCode: selectedQuotation.company?.address?.zipCode || prev.customerZipCode || '',
          customerCountry: selectedQuotation.company?.address?.country || prev.customerCountry || ''
        }));
      }
    }
  };

  const handleNewItemChange = (e) => {
    const { name, value } = e.target;
    setNewItem(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const addItem = () => {
    if (!newItem.inventory || !newItem.quantity || !newItem.sellingPrice) {
      alert('Please fill in all item fields');
      return;
    }

    const selectedInventory = inventoryItems.find(item => item._id === newItem.inventory);
    if (!selectedInventory) return;

    const itemTotal = (newItem.sellingPrice - (newItem.sellingPrice * newItem.discount / 100)) * newItem.quantity;

    const item = {
      inventory: newItem.inventory,
      quantity: parseInt(newItem.quantity),
      sellingPrice: parseFloat(newItem.sellingPrice),
      discount: parseFloat(newItem.discount),
      totalAmount: itemTotal,
      itemName: selectedInventory.itemName,
      itemCode: selectedInventory.itemCode
    };

    setFormData(prev => ({
      ...prev,
      items: [...prev.items, item]
    }));

    setNewItem({
      inventory: '',
      quantity: 1,
      sellingPrice: 0,
      discount: 0
    });
    setItemSearchQuery(''); // Clear search query as requested

    // Automatically focus on the Select Item field
    if (itemInputRef.current) {
      itemInputRef.current.focus();
    }
  };

  const removeItem = (index) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  const calculateTotals = () => {
    const subtotal = formData.items.reduce((sum, item) => sum + (item.sellingPrice * item.quantity), 0);
    const totalDiscount = formData.items.reduce((sum, item) => sum + (item.sellingPrice * item.discount / 100 * item.quantity), 0);
    const deliveryCharge = Number(formData.deliveryCharge || 0);
    const additionalCharges = Number(formData.additionalCharges || 0);
    const previousBalance = Number(formData.previousBalance || 0);
    const totalAmount = subtotal - totalDiscount + deliveryCharge + additionalCharges + previousBalance;
    return { subtotal, totalDiscount, deliveryCharge, additionalCharges, previousBalance, totalAmount };
  };

  const handleCustomerSearch = (e) => {
    const value = e.target.value;
    setCustomerSearch(value);
    setFormData(prev => ({ ...prev, customerName: value }));

    if (value.length >= 2) {
      const filtered = customers.filter(c =>
        (c.firstName || '').toLowerCase().includes(value.toLowerCase()) ||
        (c.lastName || '').toLowerCase().includes(value.toLowerCase()) ||
        (c.email || '').toLowerCase().includes(value.toLowerCase())
      );
      setFilteredCustomers(filtered);
      setShowCustomerDropdown(true);
      setActiveCustomerIndex(-1); // Reset active index on new search
    } else {
      setShowCustomerDropdown(false);
    }
  };

  const handleCustomerKeyDown = (e) => {
    if (!showCustomerDropdown || filteredCustomers.length === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveCustomerIndex(prev => (prev < filteredCustomers.length - 1 ? prev + 1 : prev));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveCustomerIndex(prev => (prev > 0 ? prev - 1 : 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (activeCustomerIndex >= 0 && activeCustomerIndex < filteredCustomers.length) {
        selectCustomer(filteredCustomers[activeCustomerIndex]);
      }
    }
  };

  const selectCustomer = (customer) => {
    setFormData(prev => ({
      ...prev,
      customer: customer.isQuotationOnly ? '' : customer._id,
      customerName: customer.isQuotationOnly ? customer.firstName : `${customer.firstName} ${customer.lastName}`,
      customerPhone: customer.phone || '',
      customerStreet: customer.address?.street || '',
      customerCity: customer.address?.city || '',
      customerState: customer.address?.state || '',
      customerZipCode: customer.address?.zipCode || '',
      customerCountry: customer.address?.country || '',
      company: '' // Clear company
    }));
    setCustomerSearch(`${customer.firstName} ${customer.lastName}`);
    setShowCustomerDropdown(false);
  };

  // Handle item search for autocomplete
  useEffect(() => {
    // Only filter if not using arrow keys navigation
    if (itemSearchQuery.length >= 1) { // Changed to 1 char as requested
      const filtered = inventoryItems.filter(item =>
        (item.itemName || '').toLowerCase().includes(itemSearchQuery.toLowerCase()) ||
        (item.vendor && item.vendor.toLowerCase().includes(itemSearchQuery.toLowerCase())) ||
        (item.productDescription && item.productDescription.toLowerCase().includes(itemSearchQuery.toLowerCase()))
      );
      setFilteredInventoryItems(filtered);
      setShowItemDropdown(true);
      setActiveItemIndex(-1); // Reset active index on new search
    } else {
      setFilteredInventoryItems([]);
      setShowItemDropdown(false);
    }
  }, [itemSearchQuery, inventoryItems]);

  const handleItemKeyDown = (e) => {
    if (!showItemDropdown || filteredInventoryItems.length === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveItemIndex(prev => (prev < filteredInventoryItems.length - 1 ? prev + 1 : prev));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveItemIndex(prev => (prev > 0 ? prev - 1 : 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (activeItemIndex >= 0 && activeItemIndex < filteredInventoryItems.length) {
        selectItem(filteredInventoryItems[activeItemIndex]);
      }
    }
  };

  const selectItem = (item) => {
    setNewItem(prev => ({
      ...prev,
      inventory: item._id,
      sellingPrice: item.sellingPrice || 0,
      discount: 0
    }));
    setItemSearchQuery(`${item.itemName} (${item.itemCode})`);
    setShowItemDropdown(false);
  };

  const handleItemSearchChange = (e) => {
    const value = e.target.value;
    setItemSearchQuery(value);
    setNewItem(prev => ({ ...prev, inventory: '' }));
  };

  const handleItemSelect = async (item) => {
    setNewItem(prev => ({
      ...prev,
      inventory: item._id,
      sellingPrice: item.sellingPrice || 0
    }));
    setItemSearchQuery(item.itemName);
    setShowItemDropdown(false);

    // Auto-fill company based on selected item - fetch full item data if needed
    try {
      let companyId = null;
      if (item.company?._id) {
        companyId = item.company._id;
      } else if (item.company) {
        // If company is just an ID string
        companyId = item.company;
      } else {
        // Fetch the full item to get company data
        const response = await api.get(`/api/inventory/${item._id}`);
        const fullItem = response.data.inventoryItem;
        if (fullItem?.company?._id) {
          companyId = fullItem.company._id;
        } else if (fullItem?.company) {
          companyId = fullItem.company;
        }
      }

      if (companyId) {
        setFormData(prev => ({
          ...prev,
          company: companyId
        }));
      }
    } catch (err) {
      console.error('Error loading company for item:', err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      if (formData.items.length === 0) {
        throw new Error('Please add at least one item');
      }

      const { subtotal, totalDiscount, deliveryCharge, additionalCharges, previousBalance, totalAmount } = calculateTotals();

      // Normalize amountPaid based on explicit paymentStatus if needed
      let normalizedAmountPaid = Number(formData.amountPaid ?? 0);
      if ((formData.amountPaid === '' || formData.amountPaid === null || formData.amountPaid === undefined || isNaN(normalizedAmountPaid)) && formData.paymentStatus) {
        if (formData.paymentStatus === 'full') normalizedAmountPaid = totalAmount;
        else if (formData.paymentStatus === 'due') normalizedAmountPaid = 0;
      }
      if (formData.paymentStatus === 'full') normalizedAmountPaid = totalAmount;
      if (formData.paymentStatus === 'due') normalizedAmountPaid = 0;

      const billingData = {
        ...formData,
        amountPaid: normalizedAmountPaid,
        subtotal,
        totalDiscount,
        totalAmount,
        deliveryCharge,
        dueDate: formData.dueDate || new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
        // Map flat customer fields to backend structure
        customerAddress: {
          street: formData.customerStreet,
          city: formData.customerCity,
          state: formData.customerState,
          zipCode: formData.customerZipCode,
          country: formData.customerCountry
        }
      };
      // Keep only fields backend expects (avoid sending our flat address copies)
      delete billingData.customerStreet;
      delete billingData.customerCity;
      delete billingData.customerState;
      delete billingData.customerZipCode;
      delete billingData.customerCountry;

      if (editingBilling) {
        const response = await api.put(`/api/billing/${editingBilling._id}`, billingData);
        setBillings(billings.map(b => b._id === editingBilling._id ? response.data.billing : b));
        closeModal();
      } else {
        const response = await api.post('/api/billing', billingData);
        setBillings([...billings, response.data.billing]);
        setLastCreatedBilling(response.data.billing);
        closeModal();
        setShowPrintConfirm(true); // Open Print confirmation modal
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to save billing');
    } finally {
      setLoading(false);
    }
  };

  const createFromQuotation = async (quotationId) => {
    try {
      setLoading(true);
      const response = await api.post('/api/billing/from-quotation', {
        quotationId,
        paymentMethod: 'cash',
        dueDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString()
      });
      setBillings([...billings, response.data.billing]);
    } catch (err) {
      console.error('Error creating billing from quotation:', err);
      setError('Failed to create billing from quotation');
    } finally {
      setLoading(false);
    }
  };

  const filteredBillings = billings.filter(b =>
    String(b.billingNo || '').toLowerCase().includes(search.toLowerCase()) ||
    (b.company?.name && b.company.name.toLowerCase().includes(search.toLowerCase()))
  );

  const totalPages = Math.max(1, Math.ceil(filteredBillings.length / itemsPerPage));
  const pageStart = (currentPage - 1) * itemsPerPage;
  const pageEnd = pageStart + itemsPerPage;
  const paginatedBillings = filteredBillings.slice(pageStart, pageEnd);

  if (loading && billings.length === 0) return <Loading />;

  return (
    <div className="billing-container">
      <div className="billing-header">
        <div>
          <h1 className="billing-title">Billing Management</h1>
          <p className="billing-subtitle">Create, edit, and manage billing records</p>
        </div>
        <div style={{ display: 'flex', gap: '12px' }}>
          <button className="add-billing-btn" onClick={openCreateModal}>
            <FaPlus /> Create Billing
          </button>
          {quotations.filter(q => q.status === 'accepted').length > 0 && (
            <button
              className="add-billing-btn"
              onClick={() => {
                openCreateModal();
                // Auto-select first accepted quotation
                const firstAcceptedQuotation = quotations.find(q => q.status === 'accepted');
                if (firstAcceptedQuotation) {
                  setTimeout(() => {
                    setFormData(prev => ({
                      ...prev,
                      quotation: firstAcceptedQuotation._id,
                      company: firstAcceptedQuotation.company?._id || firstAcceptedQuotation.company || '',
                      items: (firstAcceptedQuotation.items || []).map(item => ({
                        inventory: (item.inventory && item.inventory._id) ? item.inventory._id : (item.inventory || ''),
                        quantity: item.quantity,
                        sellingPrice: item.sellingPrice,
                        discount: item.discount,
                        totalAmount: item.totalAmount,
                        itemName: item.inventory?.itemName || item.itemName,
                        itemCode: item.inventory?.itemCode || item.itemCode
                      }))
                    }));
                  }, 100);
                }
              }}
              style={{ background: 'linear-gradient(135deg, #28a745 0%, #20c997 100%)' }}
            >
              <FaFileInvoice /> Create from Quotation
            </button>
          )}
        </div>
      </div>

      <div className="filters-section">
        <input
          type="text"
          placeholder="Search billings by number or company..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="search-input"
        />

        <div className="filter-row">
          <select
            value={companyFilter}
            onChange={e => setCompanyFilter(e.target.value)}
            className="filter-select"
          >
            <option value="">All Companies</option>
            {companies.map(company => (
              <option key={company._id} value={company._id}>
                {company.name}
              </option>
            ))}
          </select>

          <select
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value)}
            className="filter-select"
          >
            <option value="">All Statuses</option>
            {statusOptions.map(status => (
              <option key={status.value} value={status.value}>
                {status.label}
              </option>
            ))}
          </select>

          <button onClick={loadBillings} className="refresh-btn">
            Refresh
          </button>
        </div>

        {/* Removed development debug info block */}
      </div>

      <div className="billing-table-wrapper">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <div style={{ color: '#6c757d', fontSize: '0.9rem' }}>
            Showing {filteredBillings.length === 0 ? 0 : (pageStart + 1)}-
            {Math.min(filteredBillings.length, pageEnd)} of {filteredBillings.length}
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <label htmlFor="pageSize" style={{ color: '#6c757d', fontSize: '0.9rem' }}>Rows per page:</label>
            <select
              id="pageSize"
              className="filter-select"
              value={itemsPerPage}
              onChange={(e) => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }}
              style={{ minWidth: 100 }}
            >
              {[10, 20, 50, 100].map(sz => (
                <option key={sz} value={sz}>{sz}</option>
              ))}
            </select>
            <button
              className="btn-secondary"
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
            >
              Previous
            </button>
            <span style={{ color: '#6c757d', fontSize: '0.9rem' }}>
              Page {currentPage} of {totalPages}
            </span>
            <button
              className="btn-secondary"
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage >= totalPages}
            >
              Next
            </button>
          </div>
        </div>
        <table className="billing-table">
          <thead>
            <tr>
              <th>Billing No</th>
              <th>Customer</th>
              <th>Items</th>
              <th>Total Amount</th>
              <th>Amount Paid</th>
              <th>Due Amount</th>
              <th>Status</th>
              <th>Payment Method</th>
              <th>Payment Status</th>
              <th>Credit For (days)</th>
              <th>Due Date</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredBillings.length === 0 ? (
              <tr>
                <td colSpan={12} style={{ textAlign: 'center' }}>No billings found.</td>
              </tr>
            ) : (
              paginatedBillings.map(billing => (
                <tr key={billing._id}>
                  <td>
                    <div className="billing-number-cell">
                      <FaFileInvoice className="billing-icon" />
                      <span>{billing.billingNo}</span>
                    </div>
                  </td>
                  <td>{billing.customerName || billing.customer?.firstName || billing.company?.name || 'N/A'}</td>
                  <td>
                    <span className="items-count">
                      {billing.items?.length || 0} items
                    </span>
                  </td>
                  <td>₹{billing.totalAmount?.toFixed(2)}</td>
                  <td>₹{Number(billing.amountPaid || 0).toFixed(2)}</td>
                  <td>₹{(Math.max(0, (billing.totalAmount || 0) - (billing.amountPaid || 0))).toFixed(2)}</td>
                  <td>
                    <span className={`status-badge ${billing.status}`}>
                      {billing.status}
                    </span>
                  </td>
                  <td>{billing.paymentMethod || 'N/A'}</td>
                  <td>{(paymentStatusOptions.find(o => o.value === billing.paymentStatus)?.label) || (billing.paymentStatus || 'N/A')}</td>
                  <td>{Number.isFinite(billing.creditDays) ? billing.creditDays : (billing.creditDays ?? 'N/A')}</td>
                  <td>
                    {formatShortDate(billing.dueDate)}
                  </td>
                  <td>
                    <div className="action-buttons-row">
                      <button
                        onClick={() => handlePrint(billing)}
                        className="action-btn print"
                        title="Print Billing"
                      >
                        <FaPrint /> Print
                      </button>
                      <button
                        onClick={() => openViewModal(billing)}
                        className="action-btn view"
                        title="View Billing"
                      >
                        <FaEye /> View
                      </button>
                      <button
                        onClick={() => handleDownloadPdf(billing)}
                        className="action-btn edit"
                        title="Download PDF (Half A4)"
                      >
                        PDF
                      </button>
                      <button
                        onClick={() => openEditModal(billing)}
                        className="action-btn edit"
                        title="Edit Billing"
                      >
                        <FaEdit /> Edit
                      </button>
                      <button
                        onClick={() => handleDelete(billing._id)}
                        className="action-btn delete"
                        title="Delete Billing"
                      >
                        <FaTrash /> Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Create/Edit Modal */}
      {(showModal || showCreateModal) && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content large-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingBilling ? 'Edit Billing' : 'Create New Billing'}</h2>
              <button className="modal-close" onClick={closeModal}>
                <FaTimes />
              </button>
            </div>

            {error && (
              <div className="error-message">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="billing-form">
              <div className="form-section">
                <h3>Billing Information</h3>
                <div className="form-row">


                  <div className="form-group">
                    <label htmlFor="quotation">Quotation (Optional)</label>
                    <select
                      id="quotation"
                      name="quotation"
                      value={formData.quotation}
                      onChange={handleInputChange}
                    >
                      <option value="">Select a quotation</option>
                      {(() => {
                        const available = (quotations || []).filter(q => {
                          const companyId = q.company?._id || q.company;
                          const companyMatch = !formData.company || (companyId && companyId === formData.company);
                          const statusMatch = !q.status || !['rejected', 'expired', 'cancelled'].includes(q.status);
                          return companyMatch && statusMatch;
                        });
                        if (available.length === 0) {
                          return <option value="" disabled>No quotations available</option>;
                        }
                        return available.map(quotation => (
                          <option key={quotation._id} value={quotation._id}>
                            {(quotation.quotationNo || quotation._id.slice(-6).toUpperCase())} - {quotation.company?.name || 'Unknown Company'}
                          </option>
                        ));
                      })()}
                    </select>
                    {quotations.length > 0 && (
                      <small style={{ color: '#6c757d', fontSize: '0.8rem' }}>
                        Showing {(quotations || []).filter(q => {
                          const companyId = q.company?._id || q.company;
                          const companyMatch = !formData.company || (companyId && companyId === formData.company);
                          const statusMatch = !q.status || !['rejected', 'expired', 'cancelled'].includes(q.status);
                          return companyMatch && statusMatch;
                        }).length} quotations
                      </small>
                    )}
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="paymentMethod">Payment Method</label>
                    <select
                      id="paymentMethod"
                      name="paymentMethod"
                      value={formData.paymentMethod}
                      onChange={handleInputChange}
                    >
                      {paymentMethods.map(method => (
                        <option key={method.value} value={method.value}>
                          {method.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label htmlFor="paymentStatus">Payment Status</label>
                    <select
                      id="paymentStatus"
                      name="paymentStatus"
                      value={formData.paymentStatus}
                      onChange={handleInputChange}
                    >
                      {paymentStatusOptions.map(s => (
                        <option key={s.value} value={s.value}>{s.label}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="amountPaid">Amount Paid</label>
                    <input
                      type="number"
                      id="amountPaid"
                      name="amountPaid"
                      value={formData.amountPaid}
                      onChange={handleInputChange}
                      min="0"
                      step="0.01"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="deliveryCharge">Delivery Charges</label>
                    <input
                      type="number"
                      id="deliveryCharge"
                      name="deliveryCharge"
                      value={formData.deliveryCharge}
                      onChange={handleInputChange}
                      min="0"
                      step="0.01"
                      placeholder="0.00"
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="previousBalance">Previous Balance</label>
                    <input
                      type="number"
                      id="previousBalance"
                      name="previousBalance"
                      value={formData.previousBalance}
                      onChange={handleInputChange}
                      min="0"
                      step="0.01"
                      placeholder="0.00"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="previousBalanceDate">Previous Date</label>
                    <input
                      type="date"
                      id="previousBalanceDate"
                      name="previousBalanceDate"
                      value={formData.previousBalanceDate}
                      onChange={handleInputChange}
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="additionalCharges">Company Amount</label>
                    <input
                      type="number"
                      id="additionalCharges"
                      name="additionalCharges"
                      value={formData.additionalCharges}
                      onChange={handleInputChange}
                      min="0"
                      step="0.01"
                      placeholder="0.00"
                    />
                    <small style={{ display: 'block', color: '#6c757d', marginTop: '4px' }}>
                      Current Date: {formatDateDDMMYYYY(new Date())}
                    </small>
                  </div>

                  <div className="form-group">
                    <label htmlFor="transfoter">Transporter</label>
                    <input
                      type="text"
                      id="transfoter"
                      name="transfoter"
                      value={formData.transfoter}
                      onChange={handleInputChange}
                      placeholder="Enter transporter name"
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="dueDate">Due Date</label>
                    <input
                      type="date"
                      id="dueDate"
                      name="dueDate"
                      value={formData.dueDate}
                      onChange={handleInputChange}
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="creditDays">Credit For (days)</label>
                    <input
                      type="number"
                      id="creditDays"
                      name="creditDays"
                      value={formData.creditDays}
                      onChange={handleInputChange}
                      min="0"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="notes">Notes</label>
                  <textarea
                    id="notes"
                    name="notes"
                    value={formData.notes}
                    onChange={handleInputChange}
                    placeholder="Enter billing notes"
                    rows="3"
                  />
                </div>
              </div>

              <div className="form-section">
                <h3>Customer Details</h3>
                <div className="form-row">
                  <div className="form-group" style={{ position: 'relative' }}>
                    <label htmlFor="customerName">Customer Name</label>
                    <input
                      type="text"
                      id="customerName"
                      name="customerName"
                      value={formData.customerName}
                      onChange={handleCustomerSearch}
                      onKeyDown={handleCustomerKeyDown}
                      placeholder="Type at least 2 chars to search..."
                      autoComplete="off"
                    />
                    {showCustomerDropdown && (
                      <div className="dropdown-list" style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        right: 0,
                        zIndex: 1000,
                        backgroundColor: 'white',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        maxHeight: '200px',
                        overflowY: 'auto',
                        boxShadow: '0 2px 5px rgba(0,0,0,0.2)'
                      }}>
                        {filteredCustomers.length > 0 ? (
                          filteredCustomers.map(customer => (
                            <div
                              key={customer._id}
                              onClick={() => selectCustomer(customer)}
                              style={{
                                padding: '8px 12px',
                                cursor: 'pointer',
                                borderBottom: '1px solid #eee',
                                backgroundColor: activeCustomerIndex === filteredCustomers.indexOf(customer) ? '#e9ecef' : 'white'
                              }}
                              className="dropdown-item"
                            >
                              {customer.firstName} {customer.lastName && !customer.isQuotationOnly ? customer.lastName : ''} {customer.isQuotationOnly ? '(from Quotation)' : ''} {customer.email ? `(${customer.email})` : ''}
                            </div>
                          ))
                        ) : (
                          <div style={{ padding: '8px 12px', color: '#666' }}>No customers found</div>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="form-group">
                    <label htmlFor="customerPhone">Phone Number</label>
                    <input
                      type="text"
                      id="customerPhone"
                      name="customerPhone"
                      value={formData.customerPhone}
                      onChange={handleInputChange}
                      placeholder="Enter phone number"
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="customerStreet">Street</label>
                    <input
                      type="text"
                      id="customerStreet"
                      name="customerStreet"
                      value={formData.customerStreet}
                      onChange={handleInputChange}
                      placeholder="Street address"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="customerCity">City</label>
                    <input
                      type="text"
                      id="customerCity"
                      name="customerCity"
                      value={formData.customerCity}
                      onChange={handleInputChange}
                      placeholder="City"
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="customerState">State</label>
                    <input
                      type="text"
                      id="customerState"
                      name="customerState"
                      value={formData.customerState}
                      onChange={handleInputChange}
                      placeholder="State"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="customerZipCode">ZIP Code</label>
                    <input
                      type="text"
                      id="customerZipCode"
                      name="customerZipCode"
                      value={formData.customerZipCode}
                      onChange={handleInputChange}
                      placeholder="ZIP / Postal Code"
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="customerCountry">Country</label>
                    <input
                      type="text"
                      id="customerCountry"
                      name="customerCountry"
                      value={formData.customerCountry}
                      onChange={handleInputChange}
                      placeholder="Country"
                    />
                  </div>
                </div>
              </div>

              <div className="form-section">
                <h3>Add Items</h3>
                <div className="add-item-form">
                  <div className="form-row">
                    <div className="form-group" style={{ position: 'relative' }}>
                      <label htmlFor="inventory">Select Item</label>
                      <input
                        type="text"
                        id="inventory"
                        name="inventory"
                        ref={itemInputRef}
                        value={itemSearchQuery}
                        onChange={handleItemSearchChange}
                        onKeyDown={handleItemKeyDown}
                        onFocus={() => {
                          if (itemSearchQuery.length >= 1) {
                            setShowItemDropdown(true);
                          }
                        }}
                        onBlur={() => {
                          // Delay to allow click on dropdown item
                          setTimeout(() => setShowItemDropdown(false), 200);
                        }}
                        placeholder="Type item name..."
                        style={{ width: '100%' }}
                        autoComplete="off"
                      />
                      {showItemDropdown && filteredInventoryItems.length > 0 && (
                        <div style={{
                          position: 'absolute',
                          top: '100%',
                          left: 0,
                          right: 0,
                          backgroundColor: 'white',
                          border: '1px solid #ccc',
                          borderRadius: '4px',
                          maxHeight: '200px',
                          overflowY: 'auto',
                          zIndex: 1000,
                          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                        }}>
                          {filteredInventoryItems.map(item => (
                            <div
                              key={item._id}
                              onClick={() => handleItemSelect(item)}
                              style={{
                                padding: '8px 12px',
                                cursor: 'pointer',
                                borderBottom: '1px solid #eee',
                                backgroundColor: activeItemIndex === filteredInventoryItems.indexOf(item) ? '#e9ecef' : 'white'
                              }}
                              onMouseEnter={(e) => e.target.style.backgroundColor = '#f0f0f0'}
                              onMouseLeave={(e) => e.target.style.backgroundColor = activeItemIndex === filteredInventoryItems.indexOf(item) ? '#e9ecef' : 'white'}
                            >
                              <div style={{ fontWeight: 'bold' }}>{item.itemName}</div>
                              <div style={{ fontSize: '0.85rem', color: '#666' }}>
                                ₹{item.sellingPrice?.toFixed(2)} {item.unit ? `(${item.unit})` : ''}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    <div className="form-group">
                      <label htmlFor="quantity">Quantity</label>
                      <input
                        type="number"
                        id="quantity"
                        name="quantity"
                        value={newItem.quantity}
                        onChange={handleNewItemChange}
                        min="1"
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="sellingPrice">Selling Price</label>
                      <input
                        type="number"
                        id="sellingPrice"
                        name="sellingPrice"
                        value={newItem.sellingPrice}
                        onChange={handleNewItemChange}
                        step="0.01"
                        min="0"
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="discount">Discount (%)</label>
                      <input
                        type="number"
                        id="discount"
                        name="discount"
                        value={newItem.discount}
                        onChange={handleNewItemChange}
                        min="0"
                        max="100"
                      />
                    </div>
                  </div>

                  <button type="button" onClick={addItem} className="add-item-btn">
                    Add Item
                  </button>
                </div>

                {formData.items.length > 0 && (
                  <div className="items-list">
                    <h4>Billing Items</h4>
                    <div className="items-table">
                      <table>
                        <thead>
                          <tr>
                            <th>Item</th>
                            <th>Quantity</th>
                            <th>Price</th>
                            <th>Discount</th>
                            <th>Total</th>
                            <th>Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {formData.items.map((item, index) => (
                            <tr key={index}>
                              <td>
                                <div>
                                  <div className="item-name">{item.itemName}</div>
                                  <div className="item-code">{item.itemCode}</div>
                                </div>
                              </td>
                              <td>{item.quantity}</td>
                              <td>₹{item.sellingPrice.toFixed(2)}</td>
                              <td>{item.discount}%</td>
                              <td>₹{item.totalAmount.toFixed(2)}</td>
                              <td>
                                <button
                                  type="button"
                                  onClick={() => removeItem(index)}
                                  className="remove-item-btn"
                                >
                                  <FaTrash />
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    <div className="totals-section">
                      <div className="total-row">
                        <span>Subtotal:</span>
                        <span>₹{calculateTotals().subtotal.toFixed(2)}</span>
                      </div>
                      <div className="total-row">
                        <span>Total Discount:</span>
                        <span>₹{calculateTotals().totalDiscount.toFixed(2)}</span>
                      </div>
                      <div className="total-row">
                        <span>Delivery Charges:</span>
                        <span>₹{calculateTotals().deliveryCharge.toFixed(2)}</span>
                      </div>
                      <div className="total-row">
                        <span>Previous Balance:</span>
                        <span>₹{calculateTotals().previousBalance.toFixed(2)}</span>
                      </div>
                      <div className="total-row">
                        <span>Company Amount:</span>
                        <span>₹{calculateTotals().additionalCharges.toFixed(2)}</span>
                      </div>
                      <div className="total-row total-amount">
                        <span>Total Amount:</span>
                        <span>₹{calculateTotals().totalAmount.toFixed(2)}</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="form-actions">
                {editingBilling && (
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={handleAppendPreviousData}
                    disabled={loading}
                  >
                    Add Data
                  </button>
                )}
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={closeModal}
                  disabled={loading}
                >
                  <FaTimes /> Cancel
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={loading}
                >
                  <FaSave /> {loading ? 'Saving...' : (editingBilling ? 'Update Billing' : 'Create Billing')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* View Modal */}
      {viewingBilling && (
        <div className="modal-overlay" onClick={() => setViewingBilling(null)}>
          <div className="modal-content large-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Billing Details - {viewingBilling.billingNo}</h2>
              <button className="modal-close" onClick={() => setViewingBilling(null)}>
                <FaTimes />
              </button>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '0 16px 8px' }}>
              {/* <button
                className="btn-primary"
                onClick={() => handleDownloadPdf(viewingBilling)}
                title="Download PDF (Half A4)"
              >
                Download PDF
              </button> */}
            </div>

            <div className="billing-view-content">
              <div className="billing-info-section">
                <div className="detail-group">
                  <h3>Billing Information</h3>
                  <div className="detail-item">
                    <label>Billing Number:</label>
                    <span>{viewingBilling.billingNo}</span>
                  </div>
                  <div className="detail-item">
                    <label>Company:</label>
                    <span>{viewingBilling.company?.name || 'N/A'}</span>
                  </div>
                  <div className="detail-item">
                    <label>Customer Name:</label>
                    <span>{viewingBilling.customerName || 'N/A'}</span>
                  </div>
                  <div className="detail-item">
                    <label>Phone:</label>
                    <span>{viewingBilling.customerPhone || 'N/A'}</span>
                  </div>
                  <div className="detail-item">
                    <label>Address:</label>
                    <span>{[
                      viewingBilling.customerAddress?.street,
                      viewingBilling.customerAddress?.city,
                      viewingBilling.customerAddress?.state,
                      viewingBilling.customerAddress?.zipCode,
                      viewingBilling.customerAddress?.country
                    ].filter(Boolean).join(', ') || 'N/A'}</span>
                  </div>
                  <div className="detail-item">
                    <label>Status:</label>
                    <span className={`status-badge ${viewingBilling.status}`}>
                      {viewingBilling.status}
                    </span>
                  </div>
                  <div className="detail-item">
                    <label>Payment Method:</label>
                    <span>{viewingBilling.paymentMethod || 'N/A'}</span>
                  </div>
                  <div className="detail-item">
                    <label>Due Date:</label>
                    <span>{formatShortDate(viewingBilling.dueDate)}</span>
                  </div>
                  <div className="detail-item">
                    <label>Created Date:</label>
                    <span>{formatShortDate(viewingBilling.createdAt)}</span>
                  </div>
                  {viewingBilling.transfoter && (
                    <div className="detail-item">
                      <label>Transporter:</label>
                      <span>{viewingBilling.transfoter}</span>
                    </div>
                  )}
                </div>

                <div className="detail-group">
                  <h3>Financial Summary</h3>
                  <div className="detail-item">
                    <label>Subtotal:</label>
                    <span>₹{viewingBilling.subtotal?.toFixed(2)}</span>
                  </div>
                  <div className="detail-item">
                    <label>Total Discount:</label>
                    <span>₹{viewingBilling.totalDiscount?.toFixed(2)}</span>
                  </div>
                  <div className="detail-item">
                    <label>Delivery Charges:</label>
                    <span>₹{(viewingBilling.deliveryCharge || 0).toFixed(2)}</span>
                  </div>
                  <div className="detail-item">
                    <label>Previous Balance:</label>
                    <span>₹{(viewingBilling.previousBalance || 0).toFixed(2)}</span>
                  </div>
                  <div className="detail-item">
                    <label>Previous Date:</label>
                    <span>{viewingBilling.previousBalanceDate ? formatShortDate(viewingBilling.previousBalanceDate) : 'N/A'}</span>
                  </div>
                  <div className="detail-item">
                    <label>Company Amount:</label>
                    <span>₹{(viewingBilling.additionalCharges || 0).toFixed(2)}</span>
                  </div>
                  <div className="detail-item total-amount">
                    <label>Total Amount:</label>
                    <span>₹{viewingBilling.totalAmount?.toFixed(2)}</span>
                  </div>
                  <div className="detail-item">
                    <label>Amount Paid:</label>
                    <span>₹{Number(viewingBilling.amountPaid || 0).toFixed(2)}</span>
                  </div>
                  <div className="detail-item">
                    <label>Due Amount:</label>
                    <span>₹{(Math.max(0, (viewingBilling.totalAmount || 0) - (viewingBilling.amountPaid || 0))).toFixed(2)}</span>
                  </div>
                </div>
              </div>

              <div className="items-section">
                <h3>Billing Items</h3>
                <div className="items-table">
                  <table>
                    <thead>
                      <tr>
                        <th>Item Code</th>
                        <th>Item Name</th>
                        <th>Quantity</th>
                        <th>Price</th>
                        <th>Discount</th>
                        <th>Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {viewingBilling.items?.map((item, index) => (
                        <tr key={index}>
                          <td>{item.inventory?.itemCode || item.itemCode}</td>
                          <td>{item.inventory?.itemName || item.itemName}</td>
                          <td>{item.quantity}</td>
                          <td>₹{item.sellingPrice.toFixed(2)}</td>
                          <td>{item.discount}%</td>
                          <td>₹{item.totalAmount.toFixed(2)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {viewingBilling.notes && (
                <div className="notes-section">
                  <h3>Notes</h3>
                  <p>{viewingBilling.notes}</p>
                </div>
              )}
              <div className="action-section" style={{ display: 'flex', justifyContent: 'flex-end', paddingTop: '12px' }}>
                <button
                  className="btn-primary"
                  onClick={openLedgerForBilling}
                >
                  Add Ledger Entry
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <LedgerModal
        isOpen={showLedgerModal}
        onClose={() => setShowLedgerModal(false)}
        defaultDescription={viewingBilling ? `Billing ${viewingBilling.billingNo} adjustment` : ''}
      />

      {/* Print Confirmation Modal */}
      {showPrintConfirm && (
        <div className="modal-overlay" style={{ zIndex: 2000 }}>
          <div className="modal-content" style={{ maxWidth: '400px', textAlign: 'center', padding: '30px' }}>
            <div style={{ marginBottom: '20px' }}>
              <div style={{
                width: '60px',
                height: '60px',
                borderRadius: '50%',
                backgroundColor: '#e7f3ff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 15px',
                color: '#007bff',
                fontSize: '24px'
              }}>
                <FaPrint />
              </div>
              <h2 style={{ margin: '0 0 10px' }}>Print Billing?</h2>
              <p style={{ color: '#666', margin: 0 }}>Would you like to print the billing for <strong>{lastCreatedBilling?.billingNo}</strong> now?</p>
            </div>

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
              <button
                className="btn-secondary"
                onClick={() => {
                  setShowPrintConfirm(false);
                  setLastCreatedBilling(null);
                }}
                style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 20px' }}
              >
                <FaBan /> Cancel
              </button>
              <button
                className="btn-primary"
                onClick={() => {
                  if (lastCreatedBilling) {
                    handlePrint(lastCreatedBilling);
                  }
                  setShowPrintConfirm(false);
                  setLastCreatedBilling(null);
                }}
                style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 20px' }}
              >
                <FaCheck /> Print
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Billing;
